import React, { useEffect, useRef } from "react";
import { Lock, Sparkles, LogIn, BookOpen, UserPlus, ClipboardCheck } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { useCountdown, formatUnlockDay } from "../hooks/useCountdown";

// title: the thing that's locked, e.g. "Day 18" or "Poem #03".
// subtitle: what it contains, e.g. "Word Cards · The messenger of Allah".
// showCountdown: false for a day inside the poem the learner is currently
// on — those unlock as the week's daily cadence progresses, not on a fixed
// clock worth counting down, so this shows encouragement instead of a
// number. true (default) for a genuinely future, not-yet-reached poem.
// signInRequired: true for an anonymous visitor browsing the free-preview
// poem — nothing here is on a schedule, it just needs an account, so this
// shows a sign-in prompt (with a button, via onSignIn) instead of either.
// addChildRequired: true for a guardian who IS signed in but hasn't created
// a child yet, browsing a free-preview poem — nothing here is on a schedule
// either, and telling an already-signed-in guardian to "sign in" would be
// wrong, so this shows an "add a child" prompt (via onAddChild) instead.
// lockReason: the day's `source` value from the backend. "poem_gate" means
// the previous poem isn't finished yet — the whole poem is locked regardless
// of calendar time, so a date/countdown here would be actively misleading;
// show "complete the previous poem" instead, via previousPoemNumber.
// "release_not_started" (bug fix, 2026-08-24; renamed from
// "attendance_pending" the same day — attendance no longer has any unlock
// authority at all, see compute_release_state's school_paced param and
// POST /teacher/start-release): a teacher-owned learner whose class
// hasn't had "Start from Today" pressed yet never unlocks on a calendar at
// all — but `unlocks_at` is still computed and sent even for these days
// (nothing else reads it), so without this case the dialog fell through to
// the normal countdown branch and showed a real "Unlocks on Monday" timer —
// actively wrong, since that date will come and go with nothing opening
// until the teacher starts the class (or unlocks the day by hand). Shown
// instead: a plain "ask your teacher" message, no countdown.
export default function UnlockCountdownDialog({ open, onOpenChange, title, subtitle, unlocksAt, showCountdown = true, signInRequired = false, addChildRequired = false, onSignIn, onAddChild, onUnlocked, lockReason, previousPoemNumber }) {
    const isPoemGate = lockReason === "poem_gate";
    const isReleaseNotStarted = lockReason === "release_not_started";
    const skipCountdown = !showCountdown || signInRequired || addChildRequired || isPoemGate || isReleaseNotStarted;
    const countdown = useCountdown(skipCountdown ? null : unlocksAt, open && !skipCountdown);

    // The moment the countdown actually hits zero, pull fresh data and close
    // — nobody should have to manually refresh the page to see the thing
    // they were just watching a timer for finish unlocking.
    const firedRef = useRef(false);
    useEffect(() => {
        if (!open) { firedRef.current = false; return; }
        if (!skipCountdown && countdown.done && unlocksAt && !firedRef.current) {
            firedRef.current = true;
            onUnlocked?.();
        }
    }, [open, skipCountdown, countdown.done, unlocksAt, onUnlocked]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-white rounded-[2rem] border border-honeySoft p-8 max-w-sm text-center" data-testid="unlock-countdown-dialog">
                <div className="rounded-2xl bg-lockedBg p-4 inline-flex mx-auto">
                    <Lock className="w-8 h-8 text-lockedText" strokeWidth={2.4} />
                </div>
                <DialogTitle className="font-display text-2xl font-bold text-deepGreen mt-4">
                    {title}
                </DialogTitle>
                {subtitle && <p className="text-mocha text-sm mt-1">{subtitle}</p>}
                <span className="inline-flex items-center gap-1.5 rounded-full bg-lockedBg text-lockedText px-3 py-1 text-xs font-bold mt-3" data-testid="unlock-countdown-status">
                    <Lock className="w-3 h-3" strokeWidth={2.8} /> Locked
                </span>

                {signInRequired ? (
                    <>
                        <p className="mt-4 text-mocha">Sign in for free to unlock this and the rest of the library.</p>
                        <button
                            type="button"
                            onClick={onSignIn}
                            data-testid="unlock-countdown-signin"
                            className="mt-4 inline-flex items-center gap-2 rounded-full bg-terracotta text-white px-5 py-2.5 font-display font-semibold hover:bg-terracottaDark"
                        >
                            <LogIn className="w-4 h-4" strokeWidth={2.6} /> Sign in
                        </button>
                    </>
                ) : addChildRequired ? (
                    <>
                        <p className="mt-4 text-mocha">Add a child to unlock this and the rest of the library.</p>
                        <button
                            type="button"
                            onClick={onAddChild}
                            data-testid="unlock-countdown-addchild"
                            className="mt-4 inline-flex items-center gap-2 rounded-full bg-terracotta text-white px-5 py-2.5 font-display font-semibold hover:bg-terracottaDark"
                        >
                            <UserPlus className="w-4 h-4" strokeWidth={2.6} /> Add a child
                        </button>
                    </>
                ) : isPoemGate ? (
                    <>
                        <div className="rounded-2xl bg-honeySoft p-4 inline-flex mx-auto mt-4">
                            <BookOpen className="w-6 h-6 text-deepGreen" strokeWidth={2.4} />
                        </div>
                        <p className="mt-4 font-display text-lg font-bold text-deepGreen">
                            Complete Poem {previousPoemNumber} to unlock this poem
                        </p>
                    </>
                ) : isReleaseNotStarted ? (
                    <>
                        <div className="rounded-2xl bg-honeySoft p-4 inline-flex mx-auto mt-4">
                            <ClipboardCheck className="w-6 h-6 text-deepGreen" strokeWidth={2.4} />
                        </div>
                        <p className="mt-4 font-display text-lg font-bold text-deepGreen">
                            Ask your teacher to start the class
                        </p>
                    </>
                ) : !showCountdown ? (
                    <p className="mt-4 text-mocha">Complete today&apos;s lesson first — this opens as you keep going through the week.</p>
                ) : (
                    <>
                        <DialogDescription className="text-mocha mt-4">
                            Unlocks on
                        </DialogDescription>
                        {countdown.done ? (
                            <p className="mt-4 font-display text-lg font-bold text-brightGreen inline-flex items-center gap-2 justify-center">
                                <Sparkles className="w-5 h-5" strokeWidth={2.6} /> Unlocking now…
                            </p>
                        ) : (
                            <div className="mt-4 flex items-center justify-center" data-testid="unlock-countdown-values">
                                <div className="rounded-2xl bg-honeySoft px-6 py-4 min-w-[140px]">
                                    <p className="font-display text-3xl font-bold text-deepGreen leading-none">{formatUnlockDay(unlocksAt)}</p>
                                </div>
                            </div>
                        )}
                    </>
                )}
                <p className="text-xs text-mocha mt-6">
                    {signInRequired
                        ? "Free forever, no ads — you can create an account in seconds."
                        : addChildRequired
                        ? "Takes seconds, and every child gets their own private progress."
                        : isPoemGate
                        ? "Finish the Sing & Record day of the previous poem to move on."
                        : isReleaseNotStarted
                        ? "Your class hasn't started yet — this opens the moment your teacher gets it going."
                        : "Keep learning! Complete your current lesson first — everything you’ve already unlocked stays available."}
                </p>
            </DialogContent>
        </Dialog>
    );
}
